import { useState } from 'react';
import { Church } from '../../../mocks/churches';
import ChurchModal from './ChurchModal';

interface ChurchTableProps {
  churches: Church[];
}

export default function ChurchTable({ churches }: ChurchTableProps) {
  const [selectedChurch, setSelectedChurch] = useState<Church | null>(null);
  const [shareMenuOpen, setShareMenuOpen] = useState<number | null>(null);
  const [copySuccess, setCopySuccess] = useState<number | null>(null);

  const handleShare = (church: Church, platform: string) => {
    const churchUrl = `${window.location.origin}/church/${church.id}`;
    const shareText = `Check out ${church.name} in ${church.location}`;

    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(churchUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(churchUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + churchUrl)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(churchUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(churchUrl).then(() => {
          setCopySuccess(church.id);
          setTimeout(() => {
            setCopySuccess(null);
            setShareMenuOpen(null);
          }, 2000);
        });
        break;
    }

    if (platform !== 'copy') {
      setShareMenuOpen(null);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Church Name
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Conference
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Members
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Features
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {churches.map((church) => (
                <tr key={church.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={church.image}
                        alt={church.name}
                        className="w-12 h-12 rounded-lg object-cover object-top"
                      />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm">
                          {church.name}
                        </div>
                        {church.distance !== undefined && (
                          <div className="text-xs text-teal-600 font-medium flex items-center gap-1 mt-1">
                            <i className="ri-map-pin-line"></i>
                            {church.distance.toFixed(1)} km away
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{church.location}</div>
                    <div className="text-xs text-gray-500">{church.state}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{church.conference}</div>
                    <div className="text-xs text-gray-500">{church.union}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {church.members}+
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {church.livestream && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                          <i className="ri-live-line mr-1"></i>
                          Live
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        {church.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedChurch(church)}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        View Details
                      </button>
                      
                      <div className="relative">
                        <button
                          onClick={() => setShareMenuOpen(shareMenuOpen === church.id ? null : church.id)}
                          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-share-line"></i>
                        </button>

                        {shareMenuOpen === church.id && (
                          <>
                            <div 
                              className="fixed inset-0 z-40" 
                              onClick={() => setShareMenuOpen(null)}
                            ></div>
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                              <button
                                onClick={() => handleShare(church, 'facebook')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                              >
                                <i className="ri-facebook-fill text-blue-600"></i>
                                Facebook
                              </button>
                              <button
                                onClick={() => handleShare(church, 'twitter')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                              >
                                <i className="ri-twitter-x-fill text-gray-900"></i>
                                Twitter
                              </button>
                              <button
                                onClick={() => handleShare(church, 'whatsapp')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                              >
                                <i className="ri-whatsapp-fill text-green-600"></i>
                                WhatsApp
                              </button>
                              <button
                                onClick={() => handleShare(church, 'email')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                              >
                                <i className="ri-mail-line text-gray-600"></i>
                                Email
                              </button>
                              <div className="border-t border-gray-100 my-1"></div>
                              <button
                                onClick={() => handleShare(church, 'copy')}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 cursor-pointer"
                              >
                                {copySuccess === church.id ? (
                                  <>
                                    <i className="ri-check-line text-green-600"></i>
                                    <span className="text-green-600">Link Copied!</span>
                                  </>
                                ) : (
                                  <>
                                    <i className="ri-link text-gray-600"></i>
                                    Copy Link
                                  </>
                                )}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedChurch && (
        <ChurchModal church={selectedChurch} onClose={() => setSelectedChurch(null)} />
      )}
    </>
  );
}
